import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import './SkeletonLoader.scss';

interface SkeletonLoaderProps {
  type?: 'card' | 'text' | 'circle' | 'rect';
  width?: string | number;
  height?: string | number;
  className?: string;
  count?: number;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  type = 'rect',
  width,
  height,
  className = '',
  count = 1
}) => {
  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  const baseClass = `skeleton skeleton-${type}`;

  if (count > 1) {
    return (
      <>
        {Array.from({ length: count }).map((_, index) => (
          <div key={index} className={`${baseClass} ${className}`} style={style} />
        ))}
      </>
    );
  }

  return <div className={`${baseClass} ${className}`} style={style} />;
};

export const EventCardSkeleton: React.FC = () => {
  return (
    <div className="event-card-skeleton">
      <SkeletonLoader type="rect" height="200px" className="skeleton-image" />
      <div className="skeleton-content">
        <SkeletonLoader type="text" width="80%" height="20px" className="mb-2" />
        <SkeletonLoader type="text" width="60%" height="16px" className="mb-3" />
        <div className="d-flex justify-content-between align-items-center">
          <SkeletonLoader type="text" width="100px" height="18px" />
          <SkeletonLoader type="circle" width="24px" height="24px" />
        </div>
      </div>
    </div>
  );
};

export const EventHeaderSkeleton: React.FC = () => {
  return (
    <section className="event-header-skeleton event-detail-header">
      <div className="skeleton-banner-overlay"></div>
      <div className="header-content">
        <Container className="container-xl">
          <div className="event-info-container">
            <div className="event-thumbnail-placeholder">
              <SkeletonLoader type="rect" width="300px" height="337.5px" className="rounded-3" />
            </div>
            <div className="event-details-placeholder">
              <div className="event-title-skeleton mb-3">
                <SkeletonLoader type="text" width="80%" height="40px" className="mb-2" />
                <SkeletonLoader type="text" width="60%" height="32px" />
              </div>
              <div className="event-meta-skeleton mb-4">
                <div className="d-flex gap-4 flex-wrap">
                  <SkeletonLoader type="text" width="150px" height="20px" />
                  <SkeletonLoader type="text" width="180px" height="20px" />
                  <SkeletonLoader type="text" width="100px" height="20px" />
                </div>
              </div>
              <div className="action-container-skeleton d-flex gap-3 align-items-center">
                <SkeletonLoader type="rect" width="200px" height="60px" className="rounded-pill" />
                <SkeletonLoader type="rect" width="150px" height="50px" className="rounded" />
              </div>
            </div>
          </div>
          <div className="share-buttons-skeleton d-flex justify-content-center justify-content-md-end mt-3">
            <div className="d-flex gap-2">
              <SkeletonLoader type="circle" width="40px" height="40px" />
              <SkeletonLoader type="circle" width="40px" height="40px" />
              <SkeletonLoader type="circle" width="40px" height="40px" />
              <SkeletonLoader type="circle" width="40px" height="40px" />
            </div>
          </div>
        </Container>
      </div>
    </section>
  );
};

export const EventContentSkeleton: React.FC = () => {
  return (
    <div className="event-content-skeleton card p-4 mt-4">
      <SkeletonLoader type="text" width="90%" height="25px" className="mb-3" />
      <SkeletonLoader type="text" width="100%" height="18px" count={3} className="mb-2" />
      <SkeletonLoader type="text" width="80%" height="18px" className="mb-4" />
      <SkeletonLoader type="rect" height="200px" className="mb-4" />
      <SkeletonLoader type="text" width="70%" height="25px" className="mb-3" />
      <SkeletonLoader type="text" width="100%" height="18px" count={2} className="mb-2" />
    </div>
  );
};

export const CategoryCardSkeleton: React.FC = () => {
  return (
    <div className="category-card-skeleton">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div style={{ flex: 1 }}>
          <SkeletonLoader type="text" width="60%" height="20px" className="mb-2" />
          <SkeletonLoader type="text" width="40%" height="16px" />
        </div>
        <SkeletonLoader type="text" width="80px" height="24px" />
      </div>
      <SkeletonLoader type="text" width="100%" height="16px" className="mb-2" />
      <SkeletonLoader type="text" width="70%" height="16px" className="mb-3" />
      <SkeletonLoader type="rect" width="100%" height="48px" />
    </div>
  );
};

// Hero Search Skeleton
export const HeroSearchSkeleton: React.FC = () => {
  return (
    <section className="hero-search position-relative">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <div className="search-wrapper text-center">
              <div className="hero-title-wrapper mb-4">
                <SkeletonLoader type="text" width="60%" height="48px" className="mx-auto mb-3" />
              </div>
              <div className="search-box">
                <SkeletonLoader type="rect" width="100%" height="56px" className="mb-3" />
                <div className="d-flex justify-content-center gap-2">
                  <SkeletonLoader type="rect" width="100px" height="24px" />
                  <SkeletonLoader type="rect" width="100px" height="24px" />
                  <SkeletonLoader type="rect" width="100px" height="24px" />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

// Carousel Skeleton
export const CarouselSkeleton: React.FC = () => {
  return (
    <section className="highlight-events">
      <Container>
        <SkeletonLoader type="text" width="200px" height="32px" className="mx-auto mb-4" />
        <SkeletonLoader type="rect" width="100%" height="400px" />
      </Container>
    </section>
  );
};

// Activity Card Skeleton
export const ActivityCardSkeleton: React.FC = () => {
  return (
    <div className="activity-card-skeleton">
      <SkeletonLoader type="rect" height="200px" className="skeleton-image" />
      <div className="skeleton-content">
        <SkeletonLoader type="text" width="80%" height="20px" className="mb-2" />
        <SkeletonLoader type="text" width="60%" height="16px" className="mb-3" />
        <div className="d-flex justify-content-between align-items-center">
          <SkeletonLoader type="text" width="100px" height="18px" />
          <SkeletonLoader type="circle" width="24px" height="24px" />
        </div>
      </div>
    </div>
  );
};

// Suggestion Item Skeleton
export const SuggestionItemSkeleton: React.FC = () => {
  return (
    <div className="suggestion-item-skeleton">
      <SkeletonLoader type="circle" width="48px" height="48px" className="me-3" />
      <div style={{ flex: 1 }}>
        <SkeletonLoader type="text" width="80%" height="18px" className="mb-2" />
        <SkeletonLoader type="text" width="60%" height="14px" />
      </div>
    </div>
  );
};

// Cart Item Skeleton
export const CartItemSkeleton: React.FC = () => {
  return (
    <div className="cart-item-skeleton">
      <SkeletonLoader type="rect" width="120px" height="120px" className="me-3" />
      <div style={{ flex: 1 }}>
        <SkeletonLoader type="text" width="70%" height="20px" className="mb-2" />
        <SkeletonLoader type="text" width="50%" height="16px" className="mb-2" />
        <SkeletonLoader type="text" width="40%" height="16px" className="mb-3" />
        <div className="d-flex justify-content-between align-items-center">
          <SkeletonLoader type="rect" width="100px" height="36px" />
          <SkeletonLoader type="text" width="80px" height="24px" />
        </div>
      </div>
    </div>
  );
};

// Seat Map Skeleton
export const SeatMapSkeleton: React.FC = () => {
  return (
    <div className="seat-map-skeleton">
      <SkeletonLoader type="rect" width="100%" height="300px" className="mb-3" />
      <div className="d-flex justify-content-center gap-2">
        <SkeletonLoader type="rect" width="60px" height="30px" />
        <SkeletonLoader type="rect" width="60px" height="30px" />
        <SkeletonLoader type="rect" width="60px" height="30px" />
        <SkeletonLoader type="rect" width="60px" height="30px" />
      </div>
    </div>
  );
};

// Profile Page Skeleton
export const ProfilePageSkeleton: React.FC = () => {
  return (
    <div className="profile-page-skeleton">
      <div className="profile-header-skeleton mb-4">
        <div className="d-flex align-items-center gap-3">
          <SkeletonLoader type="circle" width="80px" height="80px" />
          <div style={{ flex: 1 }}>
            <SkeletonLoader type="text" width="200px" height="24px" className="mb-2" />
            <SkeletonLoader type="text" width="150px" height="18px" />
          </div>
        </div>
      </div>
      <div className="profile-tabs-skeleton mb-4">
        <div className="d-flex gap-2">
          <SkeletonLoader type="rect" width="120px" height="40px" />
          <SkeletonLoader type="rect" width="120px" height="40px" />
          <SkeletonLoader type="rect" width="120px" height="40px" />
        </div>
      </div>
      <div className="profile-content-skeleton">
        <SkeletonLoader type="rect" width="100%" height="200px" className="mb-3" />
        <SkeletonLoader type="rect" width="100%" height="200px" />
      </div>
    </div>
  );
};

// Category Listing Skeleton
export const CategoryListingSkeleton: React.FC = () => {
  return (
    <div className="category-listing-skeleton">
      {Array.from({ length: 3 }).map((_, index) => (
        <CategoryCardSkeleton key={index} />
      ))}
    </div>
  );
};

// Events Page Hero/Filters Skeleton
export const EventsPageHeroSkeleton: React.FC = () => {
  return (
    <div className="filters-section-skeleton">
      <Row className="g-3">
        <Col lg={5} md={6}>
          <SkeletonLoader type="rect" width="100%" height="40px" />
        </Col>
        <Col lg={2} md={3}>
          <SkeletonLoader type="rect" width="100%" height="40px" />
        </Col>
        <Col lg={2} md={3}>
          <SkeletonLoader type="rect" width="100%" height="40px" />
        </Col>
        <Col lg={3} md={12}>
          <div className="d-flex gap-2">
            <SkeletonLoader type="rect" width="100px" height="40px" />
            <SkeletonLoader type="rect" width="100px" height="40px" />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SkeletonLoader;

